import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { Eye, EyeOff, Pause, Play } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../Contexts/AuthContext'
import { ssoLoginUrl } from '../../api/authApi'
import { toast } from '../../lib/toast'
import { useTheme } from '../../theme/hooks'
import ForgotPassword from './ForgotPassword'

type FormErrors = {
  username?: string
  password?: string
}

// one greeting per page load, picked at random
const GREETINGS = [
  {
    title: 'Bienvenue sur Elda',
    subtitle: 'Connectez-vous pour accéder à votre espace.',
  },
  {
    title: 'Content de vous revoir',
    subtitle: 'Connectez-vous pour retrouver votre espace.',
  },
  {
    title: 'Reprenez là où vous vous êtes arrêté',
    subtitle: 'Votre espace vous attend.',
  },
]
const pickGreeting = () =>
  GREETINGS[Math.floor(Math.random() * GREETINGS.length)]

// one of the looping drone videos in public/login, picked at random per page load
const VIDEO_COUNT = 4
const pickVideo = () =>
  `/login/${1 + Math.floor(Math.random() * VIDEO_COUNT)}.mp4`
const BACKGROUND_POSTER = '/P2080139.jpg'
// fine monochrome noise laid over the glass card
const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

// error codes set by the backend callback on /login?sso_error=
const SSO_ERRORS: Record<string, string> = {
  no_account:
    "Aucun compte ELDA n'est associé à cette adresse. Contactez votre administrateur.",
  inactive: "Ce compte a été désactivé. Veuillez contacter l'administrateur.",
  provider: 'La connexion via le fournisseur a échoué. Veuillez réessayer.',
}

const SSO_PROVIDERS = [
  {
    id: 'google',
    label: 'Google',
    logo: (
      <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z"
        />
        <path
          fill="#34A853"
          d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1C3.3 21.3 7.3 24 12 24z"
        />
        <path
          fill="#FBBC05"
          d="M5.3 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l4-3.1z"
        />
        <path
          fill="#EA4335"
          d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z"
        />
      </svg>
    ),
  },
  {
    id: 'microsoft',
    label: 'Microsoft',
    logo: (
      <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
        <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
      </svg>
    ),
  },
]

const Spinner = ({ className = '' }: { className?: string }) => (
  <svg
    className={`h-5 w-5 animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
)

const Login = () => {
  const { theme } = useTheme()
  const { login: authLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [greeting] = useState(pickGreeting)
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [remember, setRemember] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false)
  const [video] = useState(pickVideo)
  const [videoFailed, setVideoFailed] = useState<boolean>(prefersReducedMotion)
  const [focused, setFocused] = useState<string>('')
  // the provider being opened, the redirect takes a moment so the button shows it
  const [ssoPending, setSsoPending] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  // the card keeps the login form height while the shorter reset steps are shown
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelHeight, setPanelHeight] = useState<number>(0)
  useLayoutEffect(() => {
    if (!showForgotPassword && panelRef.current)
      setPanelHeight(panelRef.current.offsetHeight)
  }, [showForgotPassword])
  const [playing, setPlaying] = useState<boolean>(true)
  // the video stays hidden until its first frame plays, then fades in from the navy background
  const [videoReady, setVideoReady] = useState<boolean>(false)

  // React drops the muted attribute Safari checks before allowing autoplay, so the
  // element is muted by hand and started explicitly; a refusal (low power mode,
  // autoplay setting) leaves the poster with the play control offered
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = true
    video.defaultMuted = true
    video.setAttribute('muted', '')
    video.play()?.catch(() => setPlaying(false))
  }, [video])

  // the video loops, a click on the background pauses or resumes it
  const toggleVideo = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) video.play()
    else video.pause()
  }

  // the SSO callback lands here with a code when it refused the login
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('sso_error')
    if (!code) return
    // one toast id: StrictMode runs this effect twice in development
    toast.error(SSO_ERRORS[code] || SSO_ERRORS.provider, {
      toastId: 'sso-error',
      autoClose: 5000,
    })
    navigate('/login', { replace: true })
  }, [location.search, navigate])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)

    const validationErrors: FormErrors = {}
    if (!username.trim()) {
      validationErrors.username = "L'identifiant est requis"
    }
    if (!password.trim()) {
      validationErrors.password = 'Le mot de passe est requis'
    } else if (password.trim().length < 4) {
      validationErrors.password =
        'Le mot de passe doit avoir plus de 4 caractères'
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      Object.values(validationErrors).forEach(error => {
        toast.error(error)
      })
      setLoading(false)
      return
    }

    try {
      await authLogin({ username, password, remember })
      navigate('/', { replace: true })
    } catch (error: any) {
      if (error.isInactive) {
        toast.error(
          "Ce compte a été désactivé. Veuillez contacter l'administrateur.",
          { autoClose: 5000 },
        )
      } else if (error.message) {
        toast.error(error.message)
      } else {
        toast.error("Une erreur s'est produite lors de la connexion")
      }
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = (field: string, hasError: boolean) => ({
    backgroundColor: theme.input.bg,
    color: theme.input.text,
    borderColor: hasError
      ? theme.border.error
      : focused === field
        ? theme.brand.gold
        : theme.input.border,
  })

  // the label sits as a placeholder and floats onto the border once focused or filled
  const floatingLabel = (field: string, value: string) => {
    const up = focused === field || value !== ''
    return {
      className: `pointer-events-none absolute px-1 transition-all duration-300 ${
        up ? '-top-2 left-3 z-10 text-xs' : 'left-4 top-3 text-base'
      }`,
      style: {
        // paint only the lower half so the label matches the field below the border
        background: up
          ? `linear-gradient(transparent 50%, ${theme.input.bg} 50%)`
          : 'transparent',
        color:
          focused === field ? theme.brand.accent.text : theme.text.tertiary,
      },
    }
  }

  const linkStyle = { color: theme.brand.accent.text }
  const hoverLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.color = theme.brand.accent.textHover
  }
  const leaveLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.color = theme.brand.accent.text
  }

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{ backgroundColor: theme.brand.navy }}
    >
      <div
        className={`absolute inset-0 ${videoReady || videoFailed ? 'login-fade' : 'opacity-0'}`}
        aria-hidden="true"
        onClick={toggleVideo}
      >
        {videoFailed ? (
          <div
            className="login-drift h-full w-full bg-cover bg-center"
            style={{ backgroundImage: `url(${BACKGROUND_POSTER})` }}
          />
        ) : (
          <video
            ref={videoRef}
            className="login-drift h-full w-full object-cover"
            src={video}
            autoPlay
            muted
            loop
            playsInline
            onPlaying={() => setVideoReady(true)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={() => setVideoFailed(true)}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(11, 31, 58, 0.55) 0%, rgba(11, 31, 58, 0.1) 70%)',
          }}
        />
      </div>

      {!videoFailed && (
        <button
          type="button"
          onClick={toggleVideo}
          aria-label={playing ? 'Mettre la vidéo en pause' : 'Lire la vidéo'}
          className="absolute bottom-3 left-3 z-20 rounded-full p-2 text-white/40 transition-colors hover:text-white/80 focus-visible:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
        >
          {playing ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>
      )}

      <div className="pointer-events-none relative z-10 flex min-h-screen flex-col justify-center gap-10 p-4 sm:p-8 md:flex-row md:items-stretch md:justify-between md:p-12">
        <div className="pointer-events-auto hidden flex-col items-end justify-between gap-10 text-right text-white md:order-2 md:flex md:max-w-lg">
          <img
            src="/elda-logo-bold.svg"
            alt="Elda Logo"
            className="h-14 w-auto"
          />
          <div>
            <h1 className="text-4xl font-bold leading-tight drop-shadow-lg md:text-5xl">
              {greeting.title}
            </h1>
            <p className="mt-4 text-lg text-white/80 drop-shadow">
              {greeting.subtitle}
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="pointer-events-auto relative w-full max-w-md overflow-hidden rounded-3xl border p-8 backdrop-blur-2xl backdrop-saturate-150 md:order-1 md:self-center md:p-10"
          style={{
            background: `linear-gradient(145deg, ${theme.brand.glass.bg}, ${theme.brand.glass.bgEnd})`,
            borderColor: theme.brand.glass.border,
            boxShadow: `inset 0 1px 0 ${theme.brand.glass.highlight}, 0 25px 60px -15px rgba(0, 0, 0, 0.55)`,
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.14] mix-blend-overlay"
            style={{ backgroundImage: GRAIN }}
          />

          <div
            ref={panelRef}
            className="relative flex flex-col"
            style={{ minHeight: panelHeight || undefined }}
          >
            {/* a zoom changes level (login to reset and back), slides stay for the
                reset steps; reduced motion keeps the fade only, first paint is static */}
            <MotionConfig
              reducedMotion="user"
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {!showForgotPassword ? (
                  <motion.div
                    key="login-form"
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.04 }}
                  >
                    <div className="mb-8 flex items-start justify-between gap-4">
                      <div>
                        <h2
                          className="mb-1 text-3xl font-bold"
                          style={{ color: theme.text.primary }}
                        >
                          Connexion
                        </h2>
                        <p style={{ color: theme.text.secondary }}>
                          Accédez à votre espace
                        </p>
                      </div>
                      <img
                        src="/elda-logo.svg"
                        alt="Elda Logo"
                        className="h-9 w-auto md:hidden"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {SSO_PROVIDERS.map(provider => (
                        <a
                          key={provider.id}
                          href={ssoLoginUrl(provider.id, remember)}
                          aria-label={`Se connecter avec ${provider.label}`}
                          aria-disabled={!!ssoPending}
                          aria-busy={ssoPending === provider.id}
                          onClick={e => {
                            if (ssoPending) e.preventDefault()
                            else setSsoPending(provider.id)
                          }}
                          className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${ssoPending ? 'cursor-not-allowed opacity-70' : ''}`}
                          style={{
                            backgroundColor: theme.surface.primary,
                            borderColor: theme.border.secondary,
                            color: theme.text.primary,
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor =
                              theme.button.secondary.bgHover
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor =
                              theme.surface.primary
                          }}
                        >
                          {ssoPending === provider.id ? (
                            <Spinner className="!h-4 !w-4" />
                          ) : (
                            <>
                              {provider.logo}
                              {provider.label}
                            </>
                          )}
                        </a>
                      ))}
                    </div>

                    <div
                      className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider"
                      style={{ color: theme.text.tertiary }}
                    >
                      <span
                        className="h-px flex-1"
                        style={{ backgroundColor: theme.border.primary }}
                      />
                      Ou par e-mail
                      <span
                        className="h-px flex-1"
                        style={{ backgroundColor: theme.border.primary }}
                      />
                    </div>

                    <form className="space-y-5" onSubmit={handleSubmit}>
                      <div className={errors.username ? 'shake' : ''}>
                        <div className="relative">
                          <input
                            id="username"
                            type="text"
                            autoComplete="username"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            className="w-full rounded-xl border px-4 py-3 outline-none transition-colors"
                            style={inputStyle('username', !!errors.username)}
                            aria-invalid={!!errors.username}
                            aria-describedby={
                              errors.username ? 'username-error' : undefined
                            }
                            onFocus={() => setFocused('username')}
                            onBlur={() => setFocused('')}
                          />
                          <label
                            htmlFor="username"
                            {...floatingLabel('username', username)}
                          >
                            E-mail ou identifiant
                          </label>
                        </div>
                        {errors.username && (
                          <p
                            id="username-error"
                            role="alert"
                            className="mt-1 text-sm"
                            style={{ color: theme.status.error.text }}
                          >
                            {errors.username}
                          </p>
                        )}
                      </div>

                      <div className={errors.password ? 'shake' : ''}>
                        <div className="relative">
                          <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            className="w-full rounded-xl border px-4 py-3 pr-11 outline-none transition-colors"
                            style={inputStyle('password', !!errors.password)}
                            aria-invalid={!!errors.password}
                            aria-describedby={
                              errors.password ? 'password-error' : undefined
                            }
                            onFocus={() => setFocused('password')}
                            onBlur={() => setFocused('')}
                          />
                          <label
                            htmlFor="password"
                            {...floatingLabel('password', password)}
                          >
                            Mot de passe
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowPassword(v => !v)}
                            aria-label={
                              showPassword
                                ? 'Masquer le mot de passe'
                                : 'Afficher le mot de passe'
                            }
                            className="absolute inset-y-0 right-0 flex items-center pr-3"
                            style={{ color: theme.text.tertiary }}
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {errors.password && (
                          <p
                            id="password-error"
                            role="alert"
                            className="mt-1 text-sm"
                            style={{ color: theme.status.error.text }}
                          >
                            {errors.password}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-4 text-sm">
                        <label
                          className="flex cursor-pointer items-center gap-3"
                          style={{ color: theme.text.primary }}
                        >
                          <input
                            type="checkbox"
                            checked={remember}
                            onChange={e => setRemember(e.target.checked)}
                            className="h-5 w-5 rounded"
                            style={{ accentColor: theme.brand.gold }}
                          />
                          Rester connecté
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="transition-colors"
                          style={linkStyle}
                          onMouseEnter={hoverLink}
                          onMouseLeave={leaveLink}
                        >
                          Mot de passe oublié ?
                        </button>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        aria-busy={loading}
                        className={`flex w-full items-center justify-center rounded-xl px-4 py-3.5 font-medium shadow-md transition-colors ${loading ? 'cursor-not-allowed opacity-70' : ''}`}
                        style={{
                          backgroundColor: theme.brand.primary.bg,
                          color: theme.brand.primary.text,
                        }}
                        onMouseEnter={e => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor =
                              theme.brand.primary.bgHover
                          }
                        }}
                        onMouseLeave={e => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor =
                              theme.brand.primary.bg
                          }
                        }}
                      >
                        {loading ? (
                          <span className="inline-flex items-center">
                            <Spinner className="-ml-1 mr-3" />
                            Connexion...
                          </span>
                        ) : (
                          'Connexion'
                        )}
                      </button>
                    </form>

                    <p
                      className="mt-8 text-center text-sm"
                      style={{ color: theme.text.secondary }}
                    >
                      Pas encore de compte ?{' '}
                      <button
                        type="button"
                        onClick={() =>
                          toast.info(
                            'Contactez votre administrateur pour obtenir un accès.',
                            { toastId: 'request-access', autoClose: 3000 },
                          )
                        }
                        className="font-medium transition-colors"
                        style={linkStyle}
                        onMouseEnter={hoverLink}
                        onMouseLeave={leaveLink}
                      >
                        Demander un accès
                      </button>
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="forgot-password"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                  >
                    <ForgotPassword
                      onBack={() => setShowForgotPassword(false)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </MotionConfig>
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes login-fade {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .login-fade {
          animation: login-fade 1.6s ease-out both;
        }
        @keyframes login-drift {
          from {
            transform: scale(1.06) translate(-1.5%, -1%);
          }
          to {
            transform: scale(1.12) translate(1.5%, 1%);
          }
        }
        .login-drift {
          animation: login-drift 45s ease-in-out infinite alternate;
        }
        @media (prefers-reduced-motion: reduce) {
          .login-drift {
            animation: none;
          }
        }
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-5px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(5px);
          }
        }
        .shake {
          animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
      `}</style>
    </div>
  )
}

export default Login
